// Mobile store requests list: status filter, requested/updated datetimes. Complete is loaded when selected.
import React, { Fragment, useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { Page, Fab, List, ListItem, BlockTitle, Block, Icon, Preloader, AccordionContent } from "framework7-react";
import _ from "lodash";
import moment from "moment";

import HasRole from "modules/5_view_helpers/HasRole";
import AppNavbar from "modules/1_layout/mobile/AppNavbar";
import NotAuthorized from "modules/5_view_helpers/mobile/NotAuthorized";
import { getStoreRequests } from "../actions";
import { createStoreRequestsSubscription } from "channels/store/requests_channel";

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Requested", value: "requested" },
  { label: "Needs Approval", value: "needs_approval" },
  { label: "Approved", value: "approved" },
  { label: "PO Created", value: "po_created" },
  { label: "Ordered", value: "ordered" },
  { label: "In Store", value: "in_store" },
  { label: "Complete", value: "complete" },
  { label: "Rejected", value: "rejected" },
  { label: "Cancelled", value: "cancelled" },
];

const STATUS_FILTER_KEY = "store_requests_mobile_status";

const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY h:mm A") : "—");
const statusLabel = (status) => _.find(STATUS_OPTIONS, { value: status })?.label || _.startCase(status);
const requestParams = (status) => (status === "complete" ? { show_completed: 1 } : {});

const getInitialStatus = () => {
  const stored = localStorage.getItem(STATUS_FILTER_KEY);
  return stored && _.some(STATUS_OPTIONS, { value: stored }) ? stored : "all";
};

function mapStateToProps(state) {
  return {
    store_requests: state.store_requests,
  };
}

const StoreRequestsIndex = (props) => {
  const dispatch = useDispatch();
  const subscriptionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(getInitialStatus);
  const { store_requests } = props;
  const includeComplete = statusFilter === "complete";

  const list = _.filter(_.orderBy(_.values(store_requests || {}), ["created_at"], ["desc"]), (r) => {
    if (statusFilter === "all") return r.status !== "complete";
    return r.status === statusFilter;
  });

  const indexRole =
    HasRole(
      {
        resource_type: "Store::Request",
        name: ["create", "moderator"],
      },
      true,
    ) || HasRole({ name: "Show_All_Store_Requests" }, true);

  const createRole = HasRole(
    {
      resource_type: "Store::Request",
      name: ["create", "moderator"],
    },
    true,
  );

  useEffect(() => {
    if (indexRole) {
      setLoading(true);
      props.getStoreRequests(requestParams(statusFilter)).then(() => setLoading(false));
    }
  }, [includeComplete]);

  useEffect(() => {
    if (!indexRole) return;
    subscriptionRef.current = createStoreRequestsSubscription(dispatch);
    return () => {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [dispatch, indexRole]);

  const setStatus = (value) => {
    setStatusFilter(value);
    localStorage.setItem(STATUS_FILTER_KEY, value);
  };

  if (!indexRole) {
    return <NotAuthorized />;
  }

  return (
    <Page>
      <AppNavbar title="Store Requests" />

      <List>
        <ListItem
          title="Filter Status"
          smartSelect
          smartSelectParams={{
            openIn: "popover",
            closeOnSelect: true,
          }}
        >
          <select name="status" value={statusFilter} onChange={(e) => setStatus(e.target.value)}>
            {_.map(STATUS_OPTIONS, (opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </ListItem>
      </List>

      {loading && <Preloader />}
      {!loading && (
        <Fragment>
          <BlockTitle>
            {statusFilter === "all" ? "Requests" : statusLabel(statusFilter)} — {list.length}
          </BlockTitle>
          <List accordionList mediaList>
            {_.map(list, (item) => (
              <RequestRow key={item.id} item={item} />
            ))}
          </List>
          {_.isEmpty(list) && (
            <List>
              <ListItem title="No requests" />
            </List>
          )}
        </Fragment>
      )}

      {createRole && (
        <Fab position="right-bottom" slot="fixed" text="New" href="/store/requests/new">
          <Icon ios="f7:plus" aurora="f7:plus" md="material:add"></Icon>
        </Fab>
      )}
    </Page>
  );
};

const RequestRow = ({ item }) => {
  const subject = item.tool_account
    ? `Tool account${item.tool_account_employee?.display_name ? ` — ${item.tool_account_employee.display_name}` : ""}`
    : item.project?.display_name || item.mw_job_id || "—";

  return (
    <ListItem
      accordionItem
      title={`#${item.id} — ${subject}`}
      after={statusLabel(item.status)}
      subtitle={`Requested ${formatDateTime(item.created_at)}`}
      footer={item.requested_by?.name}
    >
      <AccordionContent style={{ backgroundColor: "#f5f5f5" }}>
        <Block>
          <p>
            <b>Last update:</b> {formatDateTime(item.updated_at)}
          </p>
          {item.notes && (
            <p>
              <b>Notes:</b> {item.notes}
            </p>
          )}
          <p>
            <b>Line items</b>
          </p>
          <ul className="no-margin">
            {_.map(item.line_items || item.line_items_attributes || [], (li, i) => (
              <li key={li.id || i}>
                {li.quantity ?? "—"} × {li.description || "—"}
              </li>
            ))}
            {_.isEmpty(item.line_items || item.line_items_attributes) && <li>No line items</li>}
          </ul>
        </Block>
      </AccordionContent>
    </ListItem>
  );
};

export default connect(mapStateToProps, { getStoreRequests })(StoreRequestsIndex);
